import { useCallback, useEffect, useRef, useState } from 'react';

export interface BlobPreviews {
  attach: (storedUrl: string, file: File) => string;
  detach: (storedUrl: string) => void;
  getPreview: (storedUrl: string) => string | undefined;
}

export function useBlobPreviews(): BlobPreviews {
  const [map, setMap] = useState<Record<string, string>>({});
  const ref = useRef(map);
  ref.current = map;

  const attach = useCallback((storedUrl: string, file: File) => {
    const existing = ref.current[storedUrl];
    if (existing) {
      return existing;
    }
    const blob = URL.createObjectURL(file);
    setMap((prev) => ({ ...prev, [storedUrl]: blob }));
    return blob;
  }, []);

  const detach = useCallback((storedUrl: string) => {
    const blob = ref.current[storedUrl];
    if (!blob) {
      return;
    }
    URL.revokeObjectURL(blob);
    setMap((prev) => {
      const next = { ...prev };
      delete next[storedUrl];
      return next;
    });
  }, []);

  const getPreview = useCallback((storedUrl: string) => ref.current[storedUrl], []);

  useEffect(
    () => () => {
      Object.values(ref.current).forEach((b) => URL.revokeObjectURL(b));
    },
    [],
  );

  return { attach, detach, getPreview };
}
